/**
 * Dashboard Block Styling Utilities
 * 
 * Centralized styling system for consistent dashboard blocks
 */

/**
 * Base block styling classes
 */
export const blockStyles = {
  // Main container
  container: 'flex flex-col h-full',
  
  // Header variants
  header: {
    base: 'flex items-center justify-between mb-4 pb-3 border-b border-border',
    icon: 'text-muted-foreground shrink-0',
    title: 'text-base font-semibold text-foreground',
    action: 'flex items-center gap-2 text-sm',
  },
  
  // Content container variants
  content: {
    base: 'rounded-lg bg-muted shadow-sm flex-1',
    interactive: 'transition-all hover:shadow-md hover:border-moss-primary/20 cursor-pointer',
    padding: {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    },
    minHeight: {
      xs: 'min-h-[200px]',
      sm: 'min-h-[250px]',
      md: 'min-h-[300px]',
      lg: 'min-h-[400px]',
    }
  },
  
  // Loading states
  loading: {
    container: 'flex flex-col items-center justify-center py-12',
    spinner: 'mb-3',
    text: 'text-sm text-neutral-secondary-mid',
  },
  
  // Empty states
  empty: {
    container: 'flex flex-col items-center justify-center py-12 text-center',
    icon: 'mb-3 text-neutral-secondary-mid/40',
    title: 'font-semibold text-neutral-primary mb-1',
    description: 'text-sm text-neutral-secondary-mid',
  },
  
  // Alert/Status variants
  alert: {
    base: 'rounded-lg p-3 border flex items-start gap-3',
    variants: {
      error: 'bg-red-50 dark:bg-red-900/50 border-red-200',
      warning: 'bg-yellow-50 border-yellow-200',
      success: 'bg-green-50 dark:bg-green-900/50 border-green-200',
      info: 'bg-blue-50 dark:bg-blue-900/50 border-blue-200',
      neutral: 'bg-gray-50 dark:bg-gray-900/50 border-gray-200',
    },
    dot: 'w-2 h-2 rounded-full shrink-0 mt-1',
    dotColors: {
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      success: 'bg-green-500',
      info: 'bg-blue-500',
      neutral: 'bg-gray-500',
    },
    text: {
      error: 'text-red-800',
      warning: 'text-yellow-800',
      success: 'text-green-800',
      info: 'text-blue-800',
      neutral: 'text-gray-800',
    }
  },
  
  // Metric card styles
  metric: {
    container: 'text-center p-3 rounded-lg border transition-colors',
    variants: {
      default: 'bg-gray-50 border-gray-200 hover:border-gray-300',
      primary: 'bg-moss-primary/5 border-moss-primary/20 hover:border-moss-primary/30',
      error: 'bg-red-50 border-red-200 hover:border-red-300',
      warning: 'bg-yellow-50 border-yellow-200 hover:border-yellow-300',
      success: 'bg-green-50 border-green-200 hover:border-green-300',
    },
    value: 'text-xl font-bold mb-0.5',
    valueColors: {
      default: 'text-gray-900',
      primary: 'text-moss-primary',
      error: 'text-red-700',
      warning: 'text-yellow-700',
      success: 'text-green-700',
    },
    label: 'text-xs',
    labelColors: {
      default: 'text-gray-600',
      primary: 'text-moss-primary/80',
      error: 'text-red-700',
      warning: 'text-yellow-700',
      success: 'text-green-700',
    }
  },
  
  // List item styles
  list: {
    container: 'divide-y divide-neutral-primary/10',
    item: 'py-2.5 transition-colors hover:bg-neutral-primary/5 -mx-4 px-4 rounded-sm',
    itemLabel: 'text-sm text-neutral-secondary-mid',
    itemValue: 'font-semibold text-neutral-primary',
    itemSubtext: 'text-xs text-neutral-secondary-mid mt-1',
  },
  
  // Card styles (for items in lists)
  card: {
    base: 'rounded-lg p-3 border transition-colors cursor-pointer',
    hover: 'hover:bg-gray-50',
    variants: {
      default: 'bg-white border-neutral-primary/10',
      primary: 'bg-white border-l-2 border-l-moss-primary',
      error: 'bg-white border-l-2 border-l-red-300',
      warning: 'bg-white border-l-2 border-l-yellow-300',
      success: 'bg-white border-l-2 border-l-green-300',
    }
  },
  
  // Badge/Tag styles
  badge: {
    base: 'inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium transition-colors',
    variants: {
      default: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      primary: 'bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20',
      error: 'bg-red-100 text-red-700 hover:bg-red-200',
      warning: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      success: 'bg-green-100 text-green-700 hover:bg-green-200',
    }
  },
  
  // Link styles
  link: {
    base: 'transition-colors font-medium',
    variants: {
      primary: 'text-moss-primary hover:text-moss-primary/80 hover:underline',
      secondary: 'text-neutral-secondary-mid hover:text-neutral-primary hover:underline',
    },
    withIcon: 'flex items-center gap-1',
  },
  
  // Divider
  divider: 'border-t border-neutral-primary/10 my-4',
  
  // Grid layouts
  grid: {
    cols2: 'grid grid-cols-2 gap-3',
    cols3: 'grid grid-cols-3 gap-3',
    cols4: 'grid grid-cols-4 gap-3',
  },
} as const;

/**
 * Helper function to combine classes
 */
export const cx = (...classes: (string | undefined | false | null)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Helper to get metric variant classes
 */
export const getMetricClasses = (variant: keyof typeof blockStyles.metric.variants = 'default') => {
  return cx(
    blockStyles.metric.container,
    blockStyles.metric.variants[variant]
  );
};

/**
 * Helper to get alert variant classes
 */
export const getAlertClasses = (variant: keyof typeof blockStyles.alert.variants = 'neutral') => {
  return cx(
    blockStyles.alert.base,
    blockStyles.alert.variants[variant]
  );
};

/**
 * Helper to get badge variant classes
 */
export const getBadgeClasses = (variant: keyof typeof blockStyles.badge.variants = 'default') => {
  return cx(
    blockStyles.badge.base,
    blockStyles.badge.variants[variant]
  );
};

/**
 * Helper to get card variant classes
 */
export const getCardClasses = (
  variant: keyof typeof blockStyles.card.variants = 'default',
  hover: boolean = true
) => {
  return cx(
    blockStyles.card.base,
    blockStyles.card.variants[variant],
    hover && blockStyles.card.hover
  );
};

/**
 * Reusable component class combinations
 */
export const componentClasses = {
  // Standard loading state
  loadingState: cx(
    blockStyles.loading.container,
    blockStyles.content.minHeight.md
  ),
  
  // Standard empty state
  emptyState: cx(
    blockStyles.empty.container,
    blockStyles.content.minHeight.md
  ),
  
  // Scrollable list container
  scrollableList: 'space-y-2 max-h-96 overflow-y-auto pr-2',
  
  // Stat row
  statRow: 'flex items-center justify-between',
  
  // Icon button
  iconButton: 'p-2 rounded-lg transition-colors hover:bg-moss-primary/10 text-moss-primary',
} as const;

